import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const PreferencesContext = createContext(null);

/** The six accent themes from the v2 design system. */
export const ACCENTS = [
  { key: 'yellow', label: 'Yellow', swatch: '#a16207' },
  { key: 'teal', label: 'Teal', swatch: '#0f766e' },
  { key: 'blue', label: 'Blue', swatch: '#1d4ed8' },
  { key: 'indigo', label: 'Indigo', swatch: '#4f46e5' },
  { key: 'rose', label: 'Rose', swatch: '#be123c' },
  { key: 'green', label: 'Green', swatch: '#15803d' },
];

const ACCENT_KEYS = ACCENTS.map((a) => a.key);

const read = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — preferences simply do not persist */
  }
};

function applyAccent(accent) {
  const root = document.documentElement;
  ACCENT_KEYS.forEach((k) => root.classList.remove(`theme-${k}`));
  root.classList.add(`theme-${accent}`);
}

export function PreferencesProvider({ children }) {
  const [accent, setAccentState] = useState(() => {
    const stored = read('idh.accent', 'yellow');
    return ACCENT_KEYS.includes(stored) ? stored : 'yellow';
  });
  const [density, setDensityState] = useState(() => read('idh.density', 'comfortable'));
  const [viewMode, setViewModeState] = useState(() => read('idh.view', 'list'));
  const [dashboardLayout, setDashboardLayoutState] = useState(() => read('idh.dashboard', 'list'));

  useEffect(() => {
    applyAccent(accent);
  }, [accent]);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  const setAccent = useCallback((next) => {
    if (!ACCENT_KEYS.includes(next)) return;
    setAccentState(next);
    write('idh.accent', next);
  }, []);

  const setDensity = useCallback((next) => {
    setDensityState(next);
    write('idh.density', next);
  }, []);

  const setViewMode = useCallback((next) => {
    setViewModeState(next);
    write('idh.view', next);
  }, []);

  const setDashboardLayout = useCallback((next) => {
    setDashboardLayoutState(next);
    write('idh.dashboard', next);
  }, []);

  const value = useMemo(
    () => ({
      accent,
      setAccent,
      accents: ACCENTS,
      density,
      setDensity,
      viewMode,
      setViewMode,
      dashboardLayout,
      setDashboardLayout,
    }),
    [accent, setAccent, density, setDensity, viewMode, setViewMode, dashboardLayout, setDashboardLayout],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return ctx;
}

export default PreferencesContext;
